/* eslint-disable prefer-const */
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputElement,
  Spinner,
  Text,
  Wrap,
} from '@chakra-ui/react';
import React from 'react';
import { MdArrowDropDown, MdClose, MdSearch } from 'react-icons/md';
import { pluralize } from '../../../../utils';
import MCustomIconButton from '../../MCustomIconButton';
import { MainInputProps } from './MainInput';
import { useCustomSelectInput } from './useCustomSelectInput';

export const MainSearchInput = React.forwardRef<
  HTMLInputElement,
  MainInputProps
>((props, ref) => {
  const {
    disabled,
    externalLoading,
    getTitleFromValue,
    inputProps,
    isOpen,
    isReadOnly,
    isSubtitleItem,
    items,
    loading,
    onOpen,
    onQueryChange,
    placeholder,
    query,
    renderContentInInput,
    showContentInInput,
    value,
  } = props;

  let {
    bgColor,
    borderColor,
    handleFocusInput,
    handleOnClear,
    handleToggleOpen,
    inputValue,
    rightElement,
    rightMargin,
    showClearBtn,
    styles,
  } = useCustomSelectInput(props, ref);

  if (!!query && !loading) {
    rightMargin += 14;
  }

  const searchResultsCount =
    items?.filter((item) => !isSubtitleItem(item)).length || 0;

  // `isolation` stands in for v2's `InputGroup`; see the note in MainInput.
  if (!showContentInInput) {
    return (
      <Box position="relative" isolation="isolate">
        {!isReadOnly && (
          <InputElement placement="start" pointerEvents="none" w="8" top="0">
            <Icon w="4" h="4" color="tPurple.base" asChild>
              <MdSearch />
            </Icon>
          </InputElement>
        )}
        <Input
          type="text"
          ref={ref}
          placeholder={placeholder}
          onChange={(e: any) => {
            !isOpen && onOpen(); // open if required
            onQueryChange(e.target.value);
          }}
          value={inputValue}
          onClick={() => !disabled && onOpen()}
          disabled={disabled}
          readOnly={isReadOnly}
          variant={isReadOnly ? 'readonly' : 'outline'}
          // Resting border + focus halo come from the V2 Input theme.
          // Only override colors for the "popover open" state.
          {...(isOpen
            ? {
                bg: bgColor,
                borderColor: 'tBlue.lightShade',
                color: 'tIndigo.base',
              }
            : {})}
          tabIndex={isOpen ? -1 : 0}
          pl={isReadOnly ? undefined : '8'}
          pr={`${rightMargin * 4}px`}
          _placeholder={{
            color: 'tGray.darkGrayPurple',
          }}
          cursor="pointer"
          title={inputValue}
          {...inputProps}
        />
        {!isReadOnly && (
          <InputElement
            placement="end"
            width={`${rightMargin * 4}px`}
            px="0"
            top="0"
          >
            {rightElement?.content}
            {!!query && !loading && (
              <Text fontSize="xs" mr="2" whiteSpace="nowrap">
                {searchResultsCount
                  ? `${searchResultsCount} ${pluralize(
                      'Result',
                      searchResultsCount,
                    )}`
                  : 'No Results'}
              </Text>
            )}
            {showClearBtn && (
              <MCustomIconButton
                icon={MdClose}
                aria-label="Clear"
                bg="none"
                color="tPurple.dark"
                _hover={{ bg: 'none' }}
                ml="2"
                btnSize={4}
                padding={0}
                containerSize={4}
                onClick={handleOnClear}
              />
            )}
            {loading || externalLoading ? (
              <Spinner size="xs" />
            ) : (
              <Button
                role="button"
                variant="icon"
                padding="0"
                minWidth="initial"
                _hover={{ background: 'none' }}
                _focus={{ background: 'none', boxShadow: 'none' }}
                tabIndex={-1}
              >
                <Icon fontSize="20" zIndex="1" asChild>
                  <MdArrowDropDown onClick={handleToggleOpen} />
                </Icon>
              </Button>
            )}
          </InputElement>
        )}
      </Box>
    );
  }
  return (
    <Box
      css={styles}
      // Only color the chrome when the popover is open; resting state takes
      // its border from the V2 Input theme.
      {...(isOpen ? { bg: bgColor, borderColor } : {})}
      display="flex"
      flexWrap="wrap"
      height="auto"
      position="relative"
      isolation="isolate"
      alignItems="center"
      px="1"
    >
      <Wrap gap={0.5}>
        {showContentInInput &&
          renderContentInInput &&
          renderContentInInput({ onToggleOpen: handleToggleOpen })}
        <Flex alignItems="center" flex="1" display="grid">
          <Input
            type="text"
            ref={ref}
            placeholder={placeholder}
            onChange={() => {}}
            value={getTitleFromValue(value) || ''}
            onFocus={handleFocusInput}
            disabled={disabled}
            tabIndex={isOpen ? -1 : 0}
            border="none"
            _focus={{ border: 'none' }}
            width="auto"
            bg="none"
            _placeholder={{
              color: 'tGray.darkGrayPurple',
            }}
            {...inputProps}
          />
        </Flex>
      </Wrap>
      <Box onClick={handleToggleOpen} position="absolute" right="0" bottom="1">
        {loading || externalLoading ? (
          <Spinner size="xs" />
        ) : (
          <Icon fontSize="20" zIndex="1" asChild>
            <MdArrowDropDown />
          </Icon>
        )}
      </Box>
    </Box>
  );
});
